/**
 * Verification feature Vuex module.
 *
 * Namespaced as VERIFICATION. Carries two co-existing models:
 *
 * 1. POINT MODEL (AVA-475 — primary). N verification points, each holding a
 *    design XYZ and an as-built XYZ in the WORLD frame plus an optional
 *    offset. Every result — adjusted points, signed per-axis deltas, pass/fail
 *    — is DERIVED in getters and never stored, so there is exactly one source
 *    of truth and no way for a stored verdict to drift from its inputs.
 *
 * 2. VIEWER PRE-FLIGHT + CONTEXT (AVA-337). The verified-tag isolation, crop,
 *    scan, and Context-panel pipeline that the Forge viewer components consume.
 *    Until a dedicated Verification endpoint exists, route context is composed
 *    from the authoritative Task, Tag, and Document APIs.
 *
 * The check-centric derivation getters (GET_CHECKLIST_ITEMS, GET_CHECKS,
 * GET_OVERALL_DISPLAY, …) are SUPERSEDED by the point model and survive only
 * to keep the AVA-337 scaffold components rendering. They are retired in
 * AVA-475 Task 11 together with ChecklistSteps / CheckRow / ToleranceReference.
 *
 * LOAD_VERIFICATION always refetches on route entry so task/tag/drawing
 * metadata is current. A route change resets the point session before loading
 * the next task, preventing crop and context state from leaking between tasks.
 *
 * UNITS. Every position in this state is in VIEWER units and none is ever
 * written in site metres — `utils/modelUnits.js` holds the whole contract and
 * names the four leaves allowed to convert outward. `state.unitTransform` is
 * this module's copy, frozen, committed by RESOLVE_VERIFIED_GEOMETRY.
 *
 * NAMING RULE FOR UNIT-BEARING IDENTIFIERS (AVA-475 W4.3). New and changed
 * LOCALS and PARAMETERS carry a space suffix — `centreViewer`, `deltaMm`,
 * `designSiteM` — because at a call site the space is the one thing the reader
 * cannot infer and the one thing that has already gone wrong here.
 *
 * PERSISTED KEYS ARE EXEMPT AND STAY EXEMPT. `point.design`, `point.asBuilt`
 * and their offsets keep their names even though they are viewer units. They
 * are draft-payload keys, and renaming one silently stops an existing draft
 * from loading — a real user losing real work, to buy self-documentation at a
 * call site a comment can serve instead. Documentation is cheap and drafts are
 * not.
 */
import verificationService from '@/features/Verification/service/verification.service';
import * as display from '@/features/Verification/utils/checkStatusDisplay';
import { computeTagDbIds } from '@/features/Verification/utils/computeTagDbIds';
import { expandBbox } from '@/features/Verification/utils/expandBbox';
import { pickScanForTagPoint } from '@/features/Verification/utils/pickScanForTagPoint';
import { classifyScans } from '@/features/Verification/utils/classifyScans';
import {
  adjustPoint,
  computeDeltas,
  evaluatePoint,
  hasSignificantOffset,
} from '@/features/Verification/utils/pointMath';
import {
  buildUnitTransform,
  viewerToSite,
  viewerVectorToSite,
} from '@/features/Verification/utils/modelUnits';
import {
  emptyDraft,
  mergePlaneAxes,
  resolvePendingConflict,
  draftAxisFill,
  draftToPoint,
  PLANE_3D,
} from '@/features/Verification/utils/asBuiltDraft';
import {
  applyLockRule,
  applyLocks,
  describeLockScope,
  describeMeasuredCount,
  emptyLocks,
  LOCKABLE_AXES,
  lockAxis,
  lockedAxes,
  unlockAll,
  unlockAxis,
} from '@/features/Verification/utils/axisLock';
import {
  buildReferenceDocumentItems,
  documentIdentity,
  findLinkedIsoDocument,
  getPrimaryVerificationTag,
  mergeReferenceDocuments,
  tagLocationLabel,
  tagTypeLabel,
  taskToVerificationContext,
  toDrawingDisplay,
} from '@/features/Verification/utils/verificationContext';
import { resolveVerifiedBBox } from '@/features/Verification/utils/verifiedBBox';
import { captureViewerEvidence } from '@/features/Verification/utils/evidenceCapture';
import { resolveReferenceDrawing } from '@/features/Verification/utils/referenceDrawing';
import { fetchAssetPointCloudModels } from '@/utils/mongoUtils';
import projectsService from '@/features/Admin/services/projects.service';
import levelsService from '@/features/Admin/services/levels.service';
import { ForgeProject } from '@/scripts/js/forgeModels';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

const CROP_MARGIN_FACTOR = 1.2; // +20% per side — preserve deviation visibility in compare.

const DEFAULT_TOLERANCE = { x: 3, y: 3, z: 3 }; // mm — surveyor-editable per point.
const DEFAULT_POINT_PREFIX = 'P';
const POINT_NAME_MAX_LENGTH = 48;
let contextRequestSequence = 0;

/**
 * Return the smallest unused positive display number.
 *
 * `id` remains monotonic and is never recycled because drafts and UI selection
 * refer to it. The human-facing number is a separate slot, so deleting P02
 * makes P02 available again without ever reusing the deleted point's identity.
 */
export function nextAvailablePointNumber(points = []) {
  const occupied = new Set();
  points.forEach((point) => {
    const stored = Number(point?.pointNumber);
    if (Number.isInteger(stored) && stored > 0) {
      occupied.add(stored);
      return;
    }

    // Backward compatibility for points created before `pointNumber` existed.
    const match = String(point?.label || '').match(/^P(\d+)$/i);
    if (match) occupied.add(Number(match[1]));
  });

  let candidate = 1;
  while (occupied.has(candidate)) candidate += 1;
  return candidate;
}

export function formatDefaultPointName(pointNumber) {
  return `${DEFAULT_POINT_PREFIX}${String(pointNumber).padStart(2, '0')}`;
}

function normalizePointName(value, fallback) {
  const trimmed = String(value ?? '').trim().replace(/\s+/g, ' ');
  return (trimmed || fallback).slice(0, POINT_NAME_MAX_LENGTH);
}

/**
 * Characters of a support number that name its room: `1HDD06ST4044` -> `1HDD`.
 * HPC tag numbers lead with the building/room code, and the project scans are
 * named for the same rooms (`1HDD0110ZL`), so the two share this prefix.
 * Four is what the demo data supports; it is a HEURISTIC and belongs with the
 * other naming assumptions, not buried in the cascade.
 */
const ROOM_TOKEN_LENGTH = 4;

/** How an as-built reading was obtained. Stored on the point as `asBuiltSource`. */
export const AS_BUILT_SOURCE = Object.freeze({
  CLOUD_PICK: 'cloudPick',
  MANUAL: 'manual',
});

const DEFAULT_MARKER_SETTINGS = Object.freeze({
  visible: true,
  showDesign: true,
  showAsBuilt: true,
  showLabels: true,
  scope: 'all',
  opacity: 0.9,
  // Multiplier around the user-calibrated base marker size — see
  // `MARKER_SIZE_RANGE` and the `.verification-markers` style block.
  markerSize: 1,
  hiddenPointIds: Object.freeze([]),
});

function freshMarkerSettings() {
  return {
    ...DEFAULT_MARKER_SETTINGS,
    hiddenPointIds: [],
  };
}

const DEFAULT_VISUALIZATION_SETTINGS = Object.freeze({
  model: Object.freeze({
    visible: true,
    opacity: 1,
    tintEnabled: false,
    color: '#9FC3BD',
    renderStyle: 'shaded',
    // Labelled X/Y/Z triad at the support's crop-box origin. ON by default:
    // every coordinate on this report is stated in survey axes, and a reader
    // cannot check an Eastings/Northings figure against the model without
    // being told which way those axes point. "Reset model appearance"
    // restores it to ON for the same reason.
    showAxes: true,
    // Triad appearance. Separate from the model's own `opacity`/appearance
    // because the triad is chrome drawn OVER the model, not part of it: a
    // surveyor who fades the geometry back to read the scan still needs the
    // axes at full strength, and vice versa.
    axesOpacity: 1,
    axesSize: 1,
  }),
  scan: Object.freeze({
    visible: true,
    opacity: 1,
    colorMode: 'rgb',
    color: '#FFD166',
    pointSize: 3,
    pointShape: 'square',
    edl: false,
  }),
});

function freshVisualizationSettings() {
  return {
    model: { ...DEFAULT_VISUALIZATION_SETTINGS.model },
    scan: { ...DEFAULT_VISUALIZATION_SETTINGS.scan },
  };
}

/**
 * Axis-triad slider bounds, exported so the popover's `min`/`max`/`step` and the
 * clamps in `SET_VISUALIZATION_SETTING` cannot drift apart — the same reason
 * `REPORT_OVERRIDE_MAX_LENGTH` is exported rather than written twice.
 *
 * Opacity floors at 0.2, not 0: a triad the surveyor has faded to nothing is
 * indistinguishable from one that failed to draw, and this feature has already
 * shipped two silently-invisible renderings (see `utils/axisTriad.js`). Size
 * spans half to double — enough to rescue a triad that reads too small on a
 * large support without letting it swamp the geometry it annotates.
 */
export const AXES_OPACITY_RANGE = Object.freeze({ min: 0.2, max: 1, step: 0.05 });
export const AXES_SIZE_RANGE = Object.freeze({ min: 0.5, max: 2, step: 0.1 });

/**
 * Point-marker size multiplier bounds. Same contract as `AXES_SIZE_RANGE`, and
 * exported for the same reason: the popover binds `min`/`max`/`step` from here,
 * so a bound cannot be changed in one place and missed in the other.
 *
 * 1 is the CALIBRATED base, not the original: the marker CSS carries a +50%
 * bump (2026-07-31), so the slider stays a clean 1-centred control around what
 * the user actually approved rather than around a size nobody wanted.
 */
export const MARKER_SIZE_RANGE = Object.freeze({ min: 0.5, max: 2, step: 0.1 });

/** Range input values arrive as strings; a non-numeric one falls back rather than poisoning the math with NaN. */
function clampedRange(value, range, fallback) {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? Math.min(range.max, Math.max(range.min, numeric))
    : fallback;
}

function normalizedHex(value, fallback) {
  const candidate = String(value || '').trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(candidate) ? candidate : fallback;
}

/**
 * Report header facts the surveyor may type in themselves.
 *
 * These four arrive EMPTY from the task/tag pipeline (see LOAD_VERIFICATION)
 * because no API supplies them yet, so today they print as four blank rows on
 * an otherwise complete conformance document. Until a source exists they are
 * surveyor-entered, and the entry is scoped to the report rather than written
 * back onto the tag — nothing here is authoritative enough to update a Tag with.
 *
 * A closed key list, mirroring `SET_VISUALIZATION_SETTING`'s rejection rule: a
 * typo'd key must not quietly create a field that then renders nowhere.
 */
export const REPORT_OVERRIDE_FIELDS = Object.freeze([
  'handbookNo',
  'modelVersion',
  'toleranceSpec',
  'revision',
]);

/** Exported so the input's `maxlength` and this clamp cannot drift apart. */
export const REPORT_OVERRIDE_MAX_LENGTH = 120;

function freshReportOverrides() {
  return REPORT_OVERRIDE_FIELDS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});
}

/**
 * Do two unit transforms say the same thing? Null-safe on both sides.
 *
 * Lets RESOLVE_VERIFIED_GEOMETRY re-commit only on a real change without
 * borrowing the GEOMETRY's change gate, which keys on dbIds/model/fragCount and
 * would be blind to exactly the case that matters: the same model answering
 * `getUnitScale()` with 0 on one pass and 0.001 on the next. Sharing that gate
 * would pin the transform at null forever — the cached-null bug this module
 * already learned once with `verifiedGeometry`.
 */
function sameUnitTransform(a, b) {
  if (!a || !b) return !a && !b;
  return (
    a.mmPerViewerUnit === b.mmPerViewerUnit
    && a.siteOffset?.x === b.siteOffset?.x
    && a.siteOffset?.y === b.siteOffset?.y
    && a.siteOffset?.z === b.siteOffset?.z
  );
}

const INITIAL_STATE = () => ({
  // ---- Point model (AVA-475) ----
  support: null,
  // Every coordinate on a point — `design`, `asBuilt` and both offsets — is in
  // VIEWER units: metres on some facilities, millimetres on others, whatever the
  // resolved model calls a coordinate (utils/modelUnits.js). Site metres are
  // produced at the three named leaves and never written back here.
  //
  // SESSION-ONLY, so there is no persisted schema this declaration has to agree
  // with: nothing hydrates `points` and nothing writes them out. If persistence
  // is added, the stored space is this one, and the loader owes no conversion.
  points: [],
  // Monotonic INTERNAL identity only. Human-facing P-numbers use the smallest
  // available slot and may be reused after deletion.
  pointCounter: 0,
  activePointId: null,
  activeStep: 1, // 1 = design, 2 = as-built, 3 = result
  notes: '',
  // Surveyor-typed values for the four report header facts no API supplies.
  // Session-scoped like `notes`: cleared by RESET on route leave.
  reportOverrides: freshReportOverrides(),
  // The selected reference drawing, resolved to printable pixels, or null.
  // Held as STATE rather than derived because resolving it is asynchronous
  // (fetch + decode) and the report layout is synchronous. null is the normal
  // outcome for a PDF or an unreachable file — see utils/referenceDrawing.js.
  reportDrawing: null,
  // As-built COMBINE-VIEWS draft. A cloud pick in an orthographic slice can
  // only honestly resolve the two axes that view can see, so picks accumulate
  // here (XY -> x,y | XZ -> x,z | YZ -> y,z) until all three axes are filled.
  // Held in the store rather than in SliceTabs' local data so it survives a
  // slice-tab switch, a modal open, and any re-render of the workspace.
  // `axes` are VIEWER-WORLD METRES — nothing scales until pointMath.
  // Shape: `{ pointId, axes: { x, y, z }, contributions: { [plane]: {...} } }`
  // — see utils/asBuiltDraft.js. Always REPLACED wholesale, never mutated in
  // place (Vue 2 cannot observe a key added to `contributions`).
  asBuiltDraft: emptyDraft(null),
  // Axis locks for the active capture. DELIBERATELY A SIBLING OF THE DRAFT,
  // never a field inside it — see RULE 2 in utils/axisLock.js. A locked value
  // written into `asBuiltDraft.axes` would become eligible for the agreement
  // gate, which would then compare a real pick against an assumption and report
  // it as two measurements agreeing. Locks are unioned in at commit only.
  asBuiltLocks: emptyLocks(),
  // Exact manually adjusted crop bounds, keyed by verification point id.
  // Stored as plain objects so Vuex never observes a live THREE.Box3.
  measurementCropBoundsByPointId: {},
  // Canonical workspace navigation. SliceTabs and ViewerStatusBar are both
  // controlled by these values; neither owns a competing local active state.
  activeCapturePlane: PLANE_3D,
  // Unsaved design click, shown immediately on the viewer until Save/Cancel.
  designPickPreview: null,
  markerSettings: freshMarkerSettings(),
  // Route-scoped compare appearance. Kept separate from global Display
  // Settings so a surveyor can tune model/scan contrast for one verification
  // without changing how every other viewer in AVA renders.
  visualizationSettings: freshVisualizationSettings(),
  // ---- Verification VM + viewer pre-flight (AVA-337) ----
  verification: null,
  loading: false,
  error: null,
  activeCheckId: null,
  // Viewer-scoped state (populated by LOAD_VERIFIED_TAGS after GET_BASE_TAGS_BY_IDS)
  verifiedTags: [], // Array of ForgeBaseTag instances with .children[].dbId
  tagsLoading: false,     // True while LOAD_VERIFIED_TAGS is in flight.
  tagsError: null,
  linkedDocuments: [],
  selectedReferenceDocumentId: null,
  documentsLoading: false,
  documentsError: null,
  contextRequestId: null,
  modelReady: false,      // Set true when GEOMETRY_LOADED_EVENT has fired on viewer.model.
  preflightApplied: false,// Set true when isolate + crop + scan orchestration completed.
  viewerError: null,      // Non-null error message when the viewer dispatch fails.
  // Facility whose scan catalogue we have already ensured. Guards
  // ENSURE_SCAN_CATALOGUE against refetching (and against two concurrent
  // callers racing) without depending on array lengths, so a facility that
  // genuinely has zero scans is not refetched on every entry.
  catalogueFacilityId: null,
  // Resolved geometry for the verified tag: `{ bbox, model, modelId, fragCount }`.
  //
  // Held in STATE rather than derived in a getter, deliberately. A getter that
  // reaches into `viewer` / `model.getInstanceTree()` depends on objects Vue
  // cannot observe, so Vuex caches its first result forever. Measured live: the
  // getter answered null (computed while geometry was still loading) while the
  // identical computation run by hand at the same instant returned a valid
  // 1380x770x253 mm box. Committing the result makes it genuinely reactive.
  verifiedGeometry: null,
  // How big a viewer unit is, frozen: `{ mmPerViewerUnit, siteScale, siteOffset }`.
  //
  // STATE for the same reason `verifiedGeometry` is — it is derived from a Forge
  // model Vue cannot observe, so a getter would cache its first answer forever.
  // Committed by RESOLVE_VERIFIED_GEOMETRY from the model that pass PROVED owns
  // the fragments; see utils/modelUnits.js for why nothing resolves units from
  // "the viewer". Null until then, and every consumer must fail VISIBLE on null
  // (deltas '—', slicing refused) rather than assume a scale — a guessed 1 is
  // what made a millimetre facility read as metres for this feature's whole life.
  //
  // The key MUST exist here: Vue 2 cannot observe a property added to an
  // existing object, so assigning it later would leave every computed that
  // reads it silently frozen on null.
  unitTransform: null,
  // Surveyor's explicit scan choice, by name. Overrides the automatic cascade.
  // null = follow the automatic pick. The automatic pick cannot be trusted on
  // every facility (no level elevations and no positioned panoramas leaves it
  // falling back to a sorted-name match, which resolved a 1HDC scan for a 1HDD
  // support), so the surveyor needs the final say over what their readings are
  // measured against.
  manualScanName: null,
  // Active orthographic / isometric view id (mirrors `verification.workspace.views[].id`).
  // The ViewerStatusBar's view combo drives this; default null until
  // LOAD_VERIFICATION commits a VM whose workspace defines `activeViewId`.
  activeView: null,
});

/**
 * The tag's own anchor point, in VIEWER units, or null.
 *
 * `getPointInView()` is the app-wide answer to "where is this tag", used by
 * `forgeService.getClosestImageToPoint` for the main viewer's nearest-360. The
 * verification cascade reads it from the same place so one tag can never resolve
 * to two different scans depending on which screen asked.
 *
 * Defensive because `verifiedTags` holds live ForgeBaseTag instances: a tag whose
 * geometry has not loaded returns null or throws, and that must degrade to the
 * bbox centre rather than break auto-selection entirely.
 */
function tagAnchorPoint(verifiedTags) {
  const tag = Array.isArray(verifiedTags) ? verifiedTags[0] : null;
  if (!tag || typeof tag.getPointInView !== 'function') return null;
  try {
    const p = tag.getPointInView();
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)) {
      return { x: p.x, y: p.y, z: p.z };
    }
  } catch (err) {
    debugWarn('[VERIFICATION] tag anchor unavailable — falling back to bbox centre', err);
  }
  return null;
}

export const VERIFICATION = {
  namespaced: true,
  state: INITIAL_STATE(),
  getters: {
    // ---- Point model (AVA-475) ----
    //
    // Single source of truth: results are DERIVED here, never stored. The only
    // persisted facts are the raw design / as-built points, their offsets and
    // the tolerance; everything else recomputes on read.

    /**
     * Every point decorated with its derived result. Per-axis only — a
     * combined 3D distance is never computed anywhere in this feature.
     * An incomplete point yields `deltas: null` and null pass flags
     * (Pending), never a false FAIL.
     */
    GET_POINTS_WITH_RESULTS(state) {
      return state.points.map((p) => {
        const designAdjusted = adjustPoint(p.design, p.designOffset);
        const asBuiltAdjusted = adjustPoint(p.asBuilt, p.asBuiltOffset);
        // Points are VIEWER units; the deviation they become is millimetres.
        // The scale comes from state, never from the live viewer — a getter has
        // no rootState, and an inline viewer read here would evaluate before the
        // model resolves and cache the wrong answer (the lesson `verifiedGeometry`
        // above records). Null scale yields null deltas, which render pending.
        const deltas = computeDeltas(
          designAdjusted,
          asBuiltAdjusted,
          state.unitTransform?.mmPerViewerUnit ?? null,
        );
        // RULE 1 (utils/axisLock.js): an axis asserted from design is never
        // PASS. It was not measured, and a fabricated pass on a conformance
        // record is worse than a wrong number because nothing about it looks
        // wrong. Applied HERE so no consumer can render a locked axis green by
        // forgetting to check — the flag simply is not true anywhere.
        const verdict = applyLockRule(evaluatePoint(deltas, p.tolerance), p.lockedAxes);
        return {
          ...p,
          lockedAxes: p.lockedAxes || [],
          // Normalized HERE rather than in GET_REPORT_MODEL so the result card
          // and the report see the same shape — a point captured before
          // evidence existed has no array at all, and every consumer would
          // otherwise need its own `|| []`.
          evidence: p.evidence || [],
          lockScope: describeLockScope(p.lockedValues || {}),
          designAdjusted,
          asBuiltAdjusted,
          deltas,
          ...verdict,
          isComplete: !!(p.design && p.asBuilt),
          // Step 4 trigger: only when BOTH sides carry a non-zero offset.
          showOffsetCheck: hasOffset(p.designOffset) && hasOffset(p.asBuiltOffset),
        };
      });
    },

    GET_ACTIVE_POINT(state, getters) {
      return getters.GET_POINTS_WITH_RESULTS.find((p) => p.id === state.activePointId) ?? null;
    },

    GET_MEASUREMENT_CROP_FOR_POINT: (state) => (pointId) => (
      pointId ? state.measurementCropBoundsByPointId[pointId] || null : null
    ),

    GET_ACTIVE_MEASUREMENT_CROP(state) {
      return state.activePointId
        ? state.measurementCropBoundsByPointId[state.activePointId] || null
        : null;
    },

    /**
     * How big a viewer unit is, or null while it is still being resolved.
     *
     * THE ONLY WAY A COMPONENT MAY OBTAIN THE SCALE. Utils take it as a
     * parameter threaded from their component caller; nothing anywhere reads it
     * off the live viewer (utils/modelUnits.js explains why that is unsafe on
     * this route). Null means "not known yet", and consumers must show that
     * rather than substituting a default.
     */
    GET_UNIT_TRANSFORM(state) {
      return state.unitTransform;
    },

    /**
     * Overall verdict across the COMPLETE points only — an unfinished point
     * must never drag the support to OUT OF TOLERANCE, and a measured FAIL is
     * never masked by one that is still pending.
     *
     * A complete point can still be NOT YET EVALUABLE: `pass` is null while the
     * unit transform is unresolved (deltas are null, so `evaluatePoint` returns
     * a null verdict), when no tolerance is set, and permanently for a point
     * whose every axis is locked (RULE 1, utils/axisLock.js — nothing was
     * measured, so nothing passed). Null is falsy, so aggregating it with a
     * bare `every` would report 'OUT OF TOLERANCE' — a FAIL on a conformance
     * document for a point that has not been judged.
     *
     * The null verdicts are not all the same kind of pending, so they do not
     * all read the same way:
     *   FAIL FIRST   one evaluated out-of-tolerance point fails the support,
     *                whatever is still pending beside it.
     *   BY DESIGN    a fully-locked point is excluded from the verdict — held
     *                instead, it would pin the support at pending forever.
     *   WORK OWED    any other null (transform pending, tolerance unset)
     *                blocks WITHIN TOLERANCE until it resolves.
     */
    GET_OVERALL_STATUS(state, getters) {
      const pts = getters.GET_POINTS_WITH_RESULTS.filter((p) => p.isComplete);
      if (!pts.length) return 'INCOMPLETE';

      const evaluated = pts.filter((p) => p.pass === true || p.pass === false);
      if (evaluated.some((p) => p.pass === false)) return 'OUT OF TOLERANCE';

      const fullyLocked = (p) => (p.lockedAxes?.length ?? 0) >= LOCKABLE_AXES.length;
      const owed = pts.some((p) => p.pass !== true && p.pass !== false && !fullyLocked(p));
      if (owed || !evaluated.length) return 'INCOMPLETE';

      return 'WITHIN TOLERANCE';
    },

    /**
     * Point-model counterpart of the (deprecated, check-centric)
     * GET_OVERALL_DISPLAY, in the exact object shape `OverallStatus.vue`
     * requires: `{ short, className, progress, failing, badge }`.
     *
     * GET_OVERALL_STATUS is a bare STRING and binding it straight to that prop
     * renders `overall-value--undefined` with no console error, so the mapping
     * lives here once instead of in every consumer. `className` is only ever
     * 'pass' | 'fail' | 'pending' — the three modifiers the component styles.
     */
    GET_POINT_OVERALL_DISPLAY(state, getters) {
      const points = getters.GET_POINTS_WITH_RESULTS;
      const complete = points.filter((p) => p.isComplete);
      const failing = complete.filter((p) => p.pass === false).length;
      const status = getters.GET_OVERALL_STATUS;

      let short = '—';
      let className = 'pending';
      if (status === 'WITHIN TOLERANCE') {
        short = 'PASS';
        className = 'pass';
      } else if (status === 'OUT OF TOLERANCE') {
        short = 'FAIL';
        className = 'fail';
      }

      return {
        status,
        short,
        className,
        progress: `${complete.length} of ${points.length} points complete`,
        failing: failing ? `${failing} failing` : '',
        badge: null,
      };
    },

    /** Raw as-built combine-views draft. Prefer GET_AS_BUILT_DRAFT_STATUS. */
    GET_AS_BUILT_DRAFT(state) {
      return state.asBuiltDraft;
    },

    GET_ACTIVE_CAPTURE_PLANE(state) {
      return state.activeCapturePlane || PLANE_3D;
    },

    GET_DESIGN_PICK_PREVIEW(state) {
      return state.designPickPreview;
    },

    GET_MARKER_SETTINGS(state) {
      return state.markerSettings;
    },

    GET_VISUALIZATION_SETTINGS(state) {
      return state.visualizationSettings;
    },

    /**
     * The draft decorated with which axes are filled, plus the provenance
     * gathered across every contributing pick. Drives the "X ok Y ok Z —"
     * indicator and gates the "Use this point" button (`isComplete`).
     *
     * A pure read of state — it never looks the selected scan up live, because
     * that changes whenever the surveyor touches the scan selector and would
     * relabel already-captured picks.
     */
    GET_AS_BUILT_DRAFT_STATUS(state) {
      const base = draftAxisFill(state.asBuiltDraft);
      const locked = lockedAxes(state.asBuiltLocks);
      if (!locked.length) return { ...base, lockedAxes: [] };

      // A locked axis is not "missing" — it is declared. Completeness therefore
      // means every FREE axis is filled. The conflict gate is untouched: it runs
      // on measured axes only, which is the whole reason locks are stored apart
      // from the draft (RULE 2, utils/axisLock.js).
      const missingAxes = (base.missingAxes || []).filter((axis) => !locked.includes(axis));
      const isComplete = missingAxes.length === 0;
      return {
        ...base,
        missingAxes,
        isComplete,
        isCommittable: isComplete && !base.pendingConflict,
        lockedAxes: locked,
      };
    },

    /** Raw lock state for the active capture. */
    GET_AS_BUILT_LOCKS(state) {
      return state.asBuiltLocks;
    },

    /**
     * The lock state plus the two HONESTY strings that must travel with any
     * result produced under a lock — see RULE 1 in utils/axisLock.js. The
     * realistic failure is not a wrong number but a report that looks like a
     * completed 3-axis verification and is a 1-axis check, so the count is
     * derived here rather than left to each consumer to remember.
     */
    GET_LOCK_SUMMARY(state) {
      const locks = state.asBuiltLocks;
      return {
        locks,
        locked: lockedAxes(locks),
        scope: describeLockScope(locks),
        measuredCount: describeMeasuredCount(locks),
        hasLocks: lockedAxes(locks).length > 0,
      };
    },

    /**
     * Free-text report notes. Exposed as a getter (rather than letting
     * NotesEditor read `state.notes`) because direct state reads in components
     * are a documented anti-pattern in this codebase — see CLAUDE.md
     * "Vuex Store - Computed Properties for State Access".
     */
    GET_NOTES(state) {
      return state.notes;
    },

    /** The surveyor's raw typed values, for the editable fields to bind to. */
    GET_REPORT_OVERRIDES(state) {
      return state.reportOverrides;
    },

    /**
     * Everything the report preview and the PDF builder need, in one shape.
     *
     * THE OVERRIDE WINS over the support value, deliberately. If a future API
     * starts supplying `handbookNo`, letting the system value win would leave
     * the preview's input box looking editable while silently discarding
     * everything typed into it. A cleared override falls back to the system
     * value rather than blanking it — you cannot delete a fact you did not
     * supply.
     *
     * ONE OF THE THREE `viewerToSite` LEAVES (utils/modelUnits.js header). A
     * report states where something IS, and a viewer coordinate is not that: on
     * Merchant the same support reads 25190 in the viewer and 15.6 on site. So
     * each point is CLONED with `designSiteM` / `asBuiltSiteM` alongside the
     * viewer-unit values the rest of the feature uses.
     *
     * CLONED, NEVER CONVERTED IN PLACE. Vuex caches getters, so the entries this
     * returns are the very objects the marker projection is drawing from
     * (VerificationViewer.vue:393) — converting one in place would move every
     * marker on screen to a site coordinate the viewer cannot plot, the moment
     * the surveyor opened the report preview. That is the failure the whole unit
     * contract exists to prevent, reintroduced at the last leaf.
     *
     * A null transform yields null site fields, and the formatters render their
     * missing-value dash. A report with a blank position column is recoverable;
     * one with unconverted viewer coordinates under a metres heading is not,
     * because nothing about it looks wrong.
     */
    GET_REPORT_MODEL(state, getters) {
      const support = state.support || {};
      const merged = REPORT_OVERRIDE_FIELDS.reduce((acc, key) => {
        acc[key] = String(state.reportOverrides[key] || '').trim() || support[key] || '';
        return acc;
      }, {});
      return {
        // Always an object, even before the task resolves: a null here would
        // silently drop everything the surveyor has already typed.
        support: { ...support, ...merged },
        points: getters.GET_POINTS_WITH_RESULTS.map((point) => ({
          ...point,
          designSiteM: viewerToSite(point.designAdjusted, state.unitTransform),
          asBuiltSiteM: viewerToSite(point.asBuiltAdjusted, state.unitTransform),
          // The offsets travel with them, or the report prints "+5.0000 E"
          // under a position column reading 15.6400 and the reader has no way
          // to know the two lines are in different units. SCALE ONLY — an
          // offset is a difference, so `globalOffset` cancels
          // (`viewerVectorToSite`).
          designOffsetSiteM: viewerVectorToSite(point.designOffset, state.unitTransform),
          asBuiltOffsetSiteM: viewerVectorToSite(point.asBuiltOffset, state.unitTransform),
        })),
        notes: state.notes,
        overall: getters.GET_OVERALL_STATUS,
        date: new Date().toLocaleDateString('en-GB'),
        // null whenever the selected reference is a PDF, an Office file, or
        // simply unreachable — the report then has no drawing page.
        drawing: state.reportDrawing,
      };
    },

    // ---- Raw getters (backend-shaped) ----

    GET_VERIFICATION(state) {
      return state.verification;
    },
    IS_LOADING(state) {
      return state.loading;
    },
    GET_ERROR(state) {
      return state.error;
    },
    // ---- Viewer pre-flight getters (verified-tag isolation + scan compare) ----
    //
    // Derived from `verifiedTags` + root state (viewer.model, pointClouds, levels,
    // projects, forgeImages). Reactive — recomputes when any dependency changes.
    // VerificationViewer.vue watches GET_VERIFIED_BBOX and applies the isolate +
    // crop + scan overlay as soon as it flips non-null.

    GET_VERIFIED_TAGS(state) {
      return state.verifiedTags;
    },

    /**
     * Flat, deduplicated list of Forge dbIds spanning all verifiedTags' children.
     * Falls back to `enumNodeChildren` subtree walk if a tag has empty children[].
     */
    GET_VERIFIED_DBIDS(state, getters, rootState) {
      const model = rootState.viewer?.model || null;
      return computeTagDbIds(state.verifiedTags, { model });
    },

    /**
     * Resolved geometry for the verified tag: `{ bbox, model, modelId, fragCount }`,
     * or null until RESOLVE_VERIFIED_GEOMETRY has succeeded.
     *
     * Reads STATE, never the viewer — see the `verifiedGeometry` state comment
     * for why deriving this in a getter cannot work.
     */
    GET_VERIFIED_GEOMETRY(state) {
      return state.verifiedGeometry;
    },

    /** Bounding box of the isolated verified tag (THREE.Box3), or null. */
    GET_VERIFIED_BBOX(state, getters) {
      return getters.GET_VERIFIED_GEOMETRY?.bbox || null;
    },

    /**
     * The model that actually owns the verified geometry — NOT necessarily
     * `viewer.model`. Isolation and fragment lookups must target this one.
     */
    GET_VERIFIED_MODEL(state, getters) {
      return getters.GET_VERIFIED_GEOMETRY?.model || null;
    },

    /**
     * Expanded bbox for crop-box application — +20% margin on each side so that
     * deviations between design and as-built stay visible inside the cropped view.
     */
    GET_VERIFIED_CROP_BOX(state, getters) {
      const bbox = getters.GET_VERIFIED_BBOX;
      if (!bbox) return null;
      return expandBbox(bbox, CROP_MARGIN_FACTOR);
    },

    /**
     * Every scan the surveyor can choose from, split into project (combined)
     * and individual (single-setup) captures.
     */
    GET_SCAN_OPTIONS(state, getters, rootState) {
      return classifyScans(rootState.pointClouds || [], rootState.projects || []);
    },

    /** The surveyor's explicit choice, or null when following the auto pick. */
    GET_MANUAL_SCAN_NAME(state) {
      return state.manualScanName;
    },

    /**
     * True when the active scan was chosen by hand rather than derived.
     * Surfaced in the UI: "which scan" and "who decided" are different questions,
     * and a reader of a conformance report needs both.
     */
    GET_SCAN_IS_MANUAL(state, getters) {
      if (!state.manualScanName) return false;
      // Resolve against the OPTION LIST, not GET_SELECTED_SCAN. When the manual
      // choice no longer exists — facility switched, catalogue reloaded —
      // GET_SELECTED_SCAN deliberately falls through to GET_AUTO_SCAN, which is
      // usually non-null. Testing it would keep answering "manual" while the
      // surveyor is in fact looking at an automatically chosen cloud, and this
      // flag is stamped onto the point as provenance.
      const { projectScans = [], individualScans = [] } = getters.GET_SCAN_OPTIONS || {};
      return [...projectScans, ...individualScans].some((s) => s.name === state.manualScanName);
    },

    /**
     * What the AUTOMATIC cascade would choose, regardless of any manual override:
     * combined (level/project) > nearest individual.
     *
     * Kept separate from GET_SELECTED_SCAN so the selector can show the surveyor
     * the suggestion alongside their own choice. On facilities with no level
     * elevations and no positioned panoramas this degrades to a sorted-name
     * match — deterministic, but not necessarily right — which is precisely why
     * it is presented as a suggestion rather than applied silently.
     *
     * ONE OF THE THREE `viewerToSite` LEAVES (utils/modelUnits.js header). The
     * cascade compares against level elevations and panorama camera positions,
     * which the API states in SITE METRES, so the tag's viewer-space position
     * has to cross that boundary — here, at the consumer, discarded immediately
     * after. Nothing site-framed is returned or stored; the previous attempt
     * converted at CAPTURE instead and put site metres into state, where every
     * viewer sink downstream then owed an inverse nobody wrote.
     */
    GET_AUTO_SCAN(state, getters, rootState) {
      const bbox = getters.GET_VERIFIED_BBOX;
      if (!bbox) return null;

      // SAME REFERENCE POINT AS THE REST OF THE APP.
      //
      // `forgeService.getClosestImageToPoint` — what the main viewer's "open the
      // 360" uses — measures from the tag's own `getPointInView()`, not from the
      // geometry's bounding-box centre. On PS-094 those are 475 mm apart in Z
      // (the anchor sits near the top of the support, the centre in the middle),
      // and the two closest scans are only 0.07-0.38 m apart in distance — so the
      // reference point, not the maths, decided the winner. Verification answered
      // Merchant_meth_028 while the main viewer opened _047 for the same support.
      //
      // Both were arithmetically right. Two different scans for one tag is still
      // wrong from the surveyor's side, and a reading is only as trustworthy as
      // its provenance, so the app must name ONE scan. Matching the established
      // app-wide behaviour is the tie-break.
      //
      // Falls back to the bbox centre when the tag cannot supply a point, which
      // is the historical behaviour and keeps the cascade working on a tag whose
      // anchor is missing.
      const anchorViewer =
        tagAnchorPoint(state.verifiedTags) || bbox.getCenter(new window.THREE.Vector3());

      // ONE conversion, deliberately AFTER the `||`: both branches are viewer
      // units, and converting inside either one leaves a sibling a later edit can
      // forget. A transform applied to one side of a comparison and not the other
      // yields a plausible-looking distance rather than an obvious error.
      //
      // Null while the pre-flight is still resolving the model — and on a model
      // that cannot state its units at all. NO SCAN, never a guessed one: the
      // cascade would otherwise compare millimetres against metre elevations and
      // name a cloud with no coverage of this support, which reads as a real
      // answer. `RESOLVE_VERIFIED_GEOMETRY` commits the transform in the same
      // pass as the geometry, and VerificationViewer's `selectedScan` watcher is
      // reactive, so the scan opens the moment it lands.
      const centreSite = viewerToSite(anchorViewer, state.unitTransform);
      if (!centreSite) return null;

      // Room token from the support number — `1HDD06ST4044` -> `1HDD`. Handed to
      // the cascade, where it GATES the name tier: a name-matched scan must
      // carry the token or that tier declines and the cascade falls through to
      // the nearest positioned scan (or null — the surveyor picks by hand).
      // The spatial tiers never read it, and with no token derivable the name
      // tier keeps its deterministic alphabetical order. It used to be only a
      // sort preference, which once resolved this 1HDD support onto a 1HDC scan
      // with zero coverage — see pickScanForTagPoint's tier-3 block.
      const supportNo = state.support?.supportNo;
      const roomToken = typeof supportNo === 'string' && supportNo.length >= ROOM_TOKEN_LENGTH
        ? supportNo.slice(0, ROOM_TOKEN_LENGTH)
        : null;
      return pickScanForTagPoint(centreSite, {
        pointClouds: rootState.pointClouds || [],
        levels: rootState.levels || [],
        projects: rootState.projects || [],
        selectedLevel: rootState.selectedLevel || null,
        forgeImages: rootState.forgeImages || [],
      }, roomToken);
    },

    /**
     * The scan every as-built reading is taken against.
     *
     * A manual choice always wins — that is the whole point of the selector, and
     * it must not be silently overridden when the tag or bbox changes.
     */
    GET_SELECTED_SCAN(state, getters) {
      if (state.manualScanName) {
        const { projectScans, individualScans } = getters.GET_SCAN_OPTIONS;
        const chosen = [...projectScans, ...individualScans].find(
          (s) => s.name === state.manualScanName,
        );
        // If the named scan is gone (facility switched, catalogue reloaded) fall
        // through to the automatic pick rather than showing nothing.
        if (chosen) return chosen.raw;
      }
      return getters.GET_AUTO_SCAN;
    },

    /**
     * Human-readable task name for breadcrumb / header surfaces.
     * Returns empty string if no verification is loaded.
     */
    GET_TASK_NAME(state) {
      return state.verification?.taskName ?? '';
    },

    // ---- UI-format derivation getters (DEPRECATED — check-centric) ----
    //
    // SUPERSEDED by the point model above. These derive purely from
    // `state.verification` and exist only so the AVA-337 scaffold components
    // keep rendering until AVA-475 Task 11 retires them together with
    // ChecklistSteps.vue, CheckRow.vue and ToleranceReference.vue.
    // Do not build anything new on them.

    /**
     * Context chips use live Task status and actual viewer-source readiness.
     * A missing status is omitted rather than replaced with a fake
     * "In progress" verification value.
     */
    GET_CHIPS(state, getters) {
      const chips = [];
      const status = state.verification?.taskStatus;
      if (status?.name) {
        const statusType = String(status.statusType || '').toLowerCase();
        chips.push({
          label: status.name,
          variant: statusType === 'done'
            ? 'pass'
            : (statusType === 'inprogress' ? 'progress' : 'default'),
        });
      }

      const hasModel = getters.GET_VERIFIED_DBIDS.length > 0;
      const hasScan = !!getters.GET_SELECTED_SCAN;
      if (hasModel || hasScan) {
        chips.push({
          label: hasModel && hasScan ? 'Scan + model' : (hasScan ? 'Scan only' : 'Model only'),
          variant: hasModel && hasScan ? 'pass' : 'progress',
        });
      }
      return chips;
    },

    /**
     * Tag header from the real tag response. The thumbnail is the resolved
     * per-facility URL returned by the Tags API.
     */
    GET_SUPPORT_HEADER(state) {
      const tag = getPrimaryVerificationTag(state);
      if (!tag) return null;
      const type = tagTypeLabel(tag);
      return {
        id: tag.id,
        label: tag.name || state.verification?.taskFriendlyId || 'Linked tag',
        name: type || state.verification?.taskName || '',
        thumbnailUrl: tag.thumbnailUrl || null,
        thumbnailAlt: tag.name ? `${tag.name} thumbnail` : 'Linked tag thumbnail',
      };
    },

    /** KeyValueList rows backed only by authoritative Task and Tag data. */
    GET_METADATA_ITEMS(state) {
      const tag = getPrimaryVerificationTag(state);
      const task = state.verification;
      if (!tag && !task) return [];
      const rows = [
        { k: 'Tag', v: tag?.name || '—', mono: true },
        { k: 'Discipline', v: tag?.discipline || task?.taskType?.name || '—' },
        { k: 'Type', v: tagTypeLabel(tag) || '—' },
        { k: 'Area / Location', v: tagLocationLabel(tag) || '—', mono: true },
        {
          k: 'Task',
          v: [task?.taskFriendlyId, task?.taskName].filter(Boolean).join(' · ') || '—',
          mono: true,
        },
      ];
      return rows;
    },

    /**
     * Right-panel OverallStatus block.
     */
    GET_OVERALL_DISPLAY(state) {
      const v = state.verification;
      if (!v) {
        return { short: '—', className: 'pending', progress: '0 of 0 checks complete', failing: '', badge: null };
      }
      const prog = v.checkProgress || {};
      const completed = (prog.passed || 0) + (prog.failed || 0) + (prog.unableToVerify || 0);
      const failingCheck = (v.checks || []).find((c) => c.status === 'Fail');
      return {
        short: display.toOverallShort(v.overallResult),
        className: display.toOverallClassName(v.overallResult),
        progress: `${completed} of ${prog.total || 0} checks complete`,
        failing: prog.failed ? `${prog.failed} failing` : '',
        badge: failingCheck ? { tag: 'NCR', sub: `${failingCheck.axis}-AXIS` } : null,
      };
    },

    GET_DRAWING_DISPLAY(state) {
      const tag = getPrimaryVerificationTag(state);
      const document = state.linkedDocuments.find(
        (candidate) => documentIdentity(candidate) === state.selectedReferenceDocumentId,
      ) || null;
      const drawing = toDrawingDisplay(document, tag);
      if (!document && state.linkedDocuments.length) {
        const count = state.linkedDocuments.length;
        drawing.name = `${count} related file${count === 1 ? '' : 's'} available`;
      }
      return {
        ...drawing,
        hasOptions: state.linkedDocuments.length > 0,
        loading: state.documentsLoading,
        error: state.documentsError,
      };
    },

    GET_REFERENCE_DOCUMENT_ITEMS(state) {
      return buildReferenceDocumentItems(
        state.linkedDocuments,
        getPrimaryVerificationTag(state),
      );
    },

    GET_SELECTED_REFERENCE_DOCUMENT_ID(state) {
      return state.selectedReferenceDocumentId;
    },

    GET_CONTEXT_STATUS(state) {
      const tag = getPrimaryVerificationTag(state);
      return {
        loading: state.loading || state.tagsLoading,
        error: state.error || state.tagsError,
        unavailable: !state.loading && !state.tagsLoading && !tag
          ? 'This task has no linked tag available for verification.'
          : '',
      };
    },

    /**
     * Right-panel ToleranceRefStrip text — reflects active check if selected,
     * else falls back to the verification-level spec.
     */
    GET_TOLERANCE_REF_STRIP(state) {
      const v = state.verification;
      if (!v) return '';
      const activeCheck = (v.checks || []).find((c) => c.id === state.activeCheckId);
      const src = activeCheck?.appliedRuleSourceDoc || v.toleranceSpecRef || '';
      const rev = v.rulePackRevision ? ` ${v.rulePackRevision}` : '';
      return src ? `${src}${rev}` : '';
    },

    /**
     * Right-panel ActionsStack "Generate Report" button label.
     *
     * Kept as a getter (rather than a hard-coded string in the component) so
     * future behaviour — e.g. "Re-generate Report" when `latestReport` is
     * already Issued, or discipline-specific verbs — lands here without
     * touching the component layer.
     */
    GET_REPORT_BUTTON_LABEL() {
      return 'Generate Report';
    },

    /**
     * Derived viewer status machine — the single source of truth the viewer
     * status bar reads. Cascades through the readiness flags in priority
     * order so the bar always reflects the *deepest* pending stage.
     *
     * Returns one of:
     *   'idle'          — no URN resolved yet (cold deep-link still waiting on facility context)
     *   'error'         — the viewer dispatch failed outright
     *   'loading_model' — Forge is downloading / indexing the model
     *   'preparing'     — model bytes in, waiting for GEOMETRY_LOADED_EVENT
     *   'loading_tags'  — model ready, verified-tag fetch still in flight
     *   'isolating'     — tags loaded but preflight (isolate + crop + scan) not applied
     *   'ready'         — everything applied, user can interact
     */
    GET_VIEWER_STATUS(state, getters, rootState) {
      if (state.viewerError) return 'error';
      const viewerState = rootState.viewerState || {};
      const currentUrn = rootState.currentUrn;
      if (!currentUrn && !viewerState.isLoading) return 'idle';
      if (viewerState.isLoading) return 'loading_model';
      if (!state.modelReady) return 'preparing';
      if (state.tagsLoading) return 'loading_tags';
      if (state.verifiedTags?.length && !state.preflightApplied) return 'isolating';
      return 'ready';
    },

    /**
     * Model-download progress as 0..100. Proxied from root viewerState.percent
     * so the status bar component doesn't need to reach into two namespaces.
     */
    GET_VIEWER_LOAD_PERCENT(state, getters, rootState) {
      return rootState.viewerState?.percent ?? 0;
    },

    /**
     * Human-readable error surface for the status bar. Empty string when no
     * error is active (caller can treat as falsy).
     */
    GET_VIEWER_ERROR(state) {
      return state.viewerError || '';
    },

    /**
     * The full list of workspace-level view items. Sourced from the VM's
     * `workspace.views` array so backend changes flow straight through.
     */
    GET_VIEW_ITEMS(state) {
      return state.verification?.workspace?.views || [];
    },

    /**
     * Currently active view id. Falls back to the VM's `workspace.activeViewId`
     * when the user hasn't yet switched via the status bar combo. Returns
     * null if neither is set.
     */
    GET_ACTIVE_VIEW(state) {
      return state.activeView || state.verification?.workspace?.activeViewId || null;
    },
  },
  mutations: {
    // ---- Point model (AVA-475) ----

    SET_SUPPORT(state, support) {
      state.support = support;
    },
    SET_MEASUREMENT_CROP_BOUNDS(state, {
      pointId, designKey, depthMm, bounds,
    }) {
      if (!pointId || !bounds?.min || !bounds?.max) return;
      state.measurementCropBoundsByPointId = {
        ...state.measurementCropBoundsByPointId,
        [pointId]: {
          designKey,
          depthMm,
          bounds: {
            min: { ...bounds.min },
            max: { ...bounds.max },
          },
        },
      };
    },
    CLEAR_MEASUREMENT_CROP_BOUNDS(state, pointId) {
      if (!pointId || !state.measurementCropBoundsByPointId[pointId]) return;
      const next = { ...state.measurementCropBoundsByPointId };
      delete next[pointId];
      state.measurementCropBoundsByPointId = next;
    },
    ADD_POINT(state, point) {
      state.points.push(point);
    },
    REMOVE_POINT(state, id) {
      state.points = state.points.filter((p) => p.id !== id);
    },
    SET_ACTIVE_POINT(state, id) {
      state.activePointId = id;
    },
    SET_ACTIVE_CAPTURE_PLANE(state, plane) {
      state.activeCapturePlane = plane || PLANE_3D;
    },
    SET_DESIGN_PICK_PREVIEW(state, preview) {
      state.designPickPreview = preview?.pointId && preview?.point
        ? {
          pointId: preview.pointId,
          point: { ...preview.point },
        }
        : null;
    },
    CLEAR_DESIGN_PICK_PREVIEW(state) {
      state.designPickPreview = null;
    },
    SET_MARKER_SETTING(state, { key, value }) {
      if (!Object.prototype.hasOwnProperty.call(DEFAULT_MARKER_SETTINGS, key)) return;
      let normalized = value;
      if (key === 'scope') normalized = value === 'current' ? 'current' : 'all';
      else if (key === 'opacity') {
        const numeric = Number(value);
        normalized = Number.isFinite(numeric) ? Math.min(1, Math.max(0.2, numeric)) : 0.9;
      } else if (key === 'markerSize') {
        // MUST stay ahead of the boolean `else` below — without an explicit
        // numeric branch a slider value of "1.4" would be coerced to `true`.
        normalized = clampedRange(value, MARKER_SIZE_RANGE, DEFAULT_MARKER_SETTINGS.markerSize);
      } else if (key === 'hiddenPointIds') {
        normalized = Array.isArray(value) ? [...new Set(value.filter(Boolean))] : [];
      } else {
        normalized = !!value;
      }
      state.markerSettings = {
        ...state.markerSettings,
        [key]: normalized,
      };
    },
    SET_VISUALIZATION_SETTING(state, { source, key, value }) {
      const defaults = DEFAULT_VISUALIZATION_SETTINGS[source];
      if (!defaults || !Object.prototype.hasOwnProperty.call(defaults, key)) return;

      let normalized = value;
      if (key === 'visible' || key === 'tintEnabled' || key === 'edl' || key === 'showAxes') {
        normalized = !!value;
      } else if (key === 'opacity') {
        const numeric = Number(value);
        normalized = Number.isFinite(numeric) ? Math.min(1, Math.max(0.05, numeric)) : 1;
      } else if (key === 'axesOpacity') {
        normalized = clampedRange(value, AXES_OPACITY_RANGE, defaults.axesOpacity);
      } else if (key === 'axesSize') {
        normalized = clampedRange(value, AXES_SIZE_RANGE, defaults.axesSize);
      } else if (key === 'color') {
        normalized = normalizedHex(value, defaults.color);
      } else if (key === 'renderStyle') {
        normalized = value === 'wireframe' ? 'wireframe' : 'shaded';
      } else if (key === 'colorMode') {
        normalized = ['rgb', 'solid', 'height'].includes(value) ? value : 'rgb';
      } else if (key === 'pointSize') {
        const numeric = Number(value);
        normalized = Number.isFinite(numeric) ? Math.min(12, Math.max(1, numeric)) : 3;
      } else if (key === 'pointShape') {
        normalized = value === 'circle' ? 'circle' : 'square';
      }

      state.visualizationSettings = {
        ...state.visualizationSettings,
        [source]: {
          ...state.visualizationSettings[source],
          [key]: normalized,
        },
      };
    },
    RESET_VISUALIZATION_SOURCE(state, source) {
      if (!DEFAULT_VISUALIZATION_SETTINGS[source]) return;
      state.visualizationSettings = {
        ...state.visualizationSettings,
        [source]: { ...DEFAULT_VISUALIZATION_SETTINGS[source] },
      };
    },
    TOGGLE_POINT_MARKER(state, pointId) {
      if (!pointId) return;
      const hidden = new Set(state.markerSettings.hiddenPointIds || []);
      if (hidden.has(pointId)) hidden.delete(pointId);
      else hidden.add(pointId);
      state.markerSettings = {
        ...state.markerSettings,
        hiddenPointIds: [...hidden],
      };
    },
    /**
     * Locks an axis to its design value, or clears it.
     *
     * The plane is passed so RULE 3 can refuse the active view's depth axis —
     * `lockAxis` is the single place that decision is made, and it returns the
     * state unchanged when it refuses, so an illegal lock can never be written
     * by going around the UI.
     */
    SET_AXIS_LOCK(state, { axis, locked, plane }) {
      const point = state.points.find((p) => p.id === state.activePointId);
      state.asBuiltLocks = locked
        ? lockAxis(state.asBuiltLocks, axis, point?.design, plane)
        : unlockAxis(state.asBuiltLocks, axis);
    },

    CLEAR_AXIS_LOCKS(state) {
      state.asBuiltLocks = unlockAll();
    },

    SET_ACTIVE_STEP(state, step) {
      state.activeStep = step;
    },
    INCREMENT_POINT_COUNTER(state) {
      state.pointCounter += 1;
    },
    PATCH_POINT(state, { id, patch }) {
      const i = state.points.findIndex((p) => p.id === id);
      if (i === -1) return;
      // Immutable replace so Vue reactivity fires on nested writes.
      state.points.splice(i, 1, { ...state.points[i], ...patch });
    },
    SET_NOTES(state, notes) {
      state.notes = notes;
    },

    /**
     * Append one captured screenshot to a point.
     *
     * THE ID IS MINTED HERE, not by the caller. Capture is asynchronous, so two
     * captures started before either finished would both read the same
     * `evidence.length` and mint the same id; assigning it inside the mutation
     * — the only place the array is touched synchronously — makes that
     * impossible.
     *
     * `width` / `height` are stored, never recomputed. The report paginator
     * needs an aspect-true image height BEFORE the image is decoded, and a
     * layout that measured images by loading them would depend on decode order.
     */
    ADD_POINT_EVIDENCE(state, { pointId, evidence }) {
      const i = state.points.findIndex((p) => p.id === pointId);
      if (i === -1 || !evidence?.dataUrl) return;
      const current = state.points[i].evidence || [];
      const entry = {
        id: `ev-${current.length + 1}-${Date.now().toString(36)}`,
        dataUrl: evidence.dataUrl,
        width: evidence.width,
        height: evidence.height,
        capturedAt: evidence.capturedAt,
        viewLabel: evidence.viewLabel || PLANE_3D,
      };
      state.points.splice(i, 1, {
        ...state.points[i],
        evidence: [...current, entry],
      });
    },

    REMOVE_POINT_EVIDENCE(state, { pointId, evidenceId }) {
      const i = state.points.findIndex((p) => p.id === pointId);
      if (i === -1) return;
      const current = state.points[i].evidence || [];
      const next = current.filter((e) => e.id !== evidenceId);
      if (next.length === current.length) return;
      state.points.splice(i, 1, { ...state.points[i], evidence: next });
    },

    /**
     * The resolved reference-drawing page, or null.
     *
     * Frozen: it carries a large data URL that nothing mutates, and Vue's
     * observer walking it would be pure waste. Reactivity comes from replacing
     * the object, never from touching it in place.
     */
    SET_REPORT_DRAWING(state, drawing) {
      state.reportDrawing = drawing ? Object.freeze(drawing) : null;
    },

    /** One surveyor-typed report header fact. Unknown keys are rejected. */
    SET_REPORT_OVERRIDE(state, { key, value }) {
      if (!REPORT_OVERRIDE_FIELDS.includes(key)) return;
      state.reportOverrides = {
        ...state.reportOverrides,
        [key]: String(value ?? '').slice(0, REPORT_OVERRIDE_MAX_LENGTH),
      };
    },

    /**
     * Fold one slice view's two visible axes into the as-built draft.
     *
     * Owns `state.asBuiltDraft` and nothing else. The merge itself is the pure
     * `mergePlaneAxes` — this mutation only stores its result, so the axis
     * bookkeeping stays node-testable. The whole draft object is REPLACED:
     * writing `contributions[plane]` in place would add a key Vue 2 cannot
     * observe, and the "X ok Y ok Z —" indicator would silently stop updating.
     *
     * Payload: `{ pointId, plane: 'XY'|'XZ'|'YZ', point: {x,y,z} (VIEWER units),
     *             scanName, scanIsManual, mmPerPx, mmPerViewerUnit }`.
     *
     * The point's own tolerance is read from state and handed to the AGREEMENT
     * GATE. Any two planes share exactly one axis, so the pick that completes a
     * point always re-measures one — and that second reading is the only
     * independent check available that both picks landed on the same physical
     * feature. Without the tolerance the gate has nothing to judge against and
     * silently degrades to last-write-wins, which is the bug it exists to
     * prevent, so it is looked up here rather than left to the caller.
     */
    MERGE_AS_BUILT_DRAFT(state, contribution) {
      const target = state.points.find((p) => p.id === contribution?.pointId) || null;
      // `mmPerViewerUnit` rides the payload (only the dispatching action can see
      // the store) but belongs with the tolerance in the gate's OPTIONS, not in
      // the contribution: a held conflict stores its contribution in the draft,
      // and a scale parked there would outlive the facility that produced it.
      state.asBuiltDraft = mergePlaneAxes(state.asBuiltDraft, contribution, {
        toleranceMm: target?.tolerance || null,
        mmPerViewerUnit: contribution?.mmPerViewerUnit ?? null,
      });
    },

    /**
     * Adjudicate a pick the agreement gate is holding.
     * `choice` is 'replace' (apply the held pick in full) or 'keep' (discard it).
     */
    RESOLVE_AS_BUILT_CONFLICT(state, choice) {
      state.asBuiltDraft = resolvePendingConflict(state.asBuiltDraft, choice);
    },

    /** Discard the as-built draft, optionally re-seeding it for a point. */
    CLEAR_AS_BUILT_DRAFT(state, pointId = null) {
      state.asBuiltDraft = emptyDraft(pointId);
      // Locks belong to ONE capture. Carrying them into the next point would
      // silently assert design values on a point nobody chose to lock.
      state.asBuiltLocks = emptyLocks();
    },

    // ---- Verification VM + viewer pre-flight (AVA-337) ----

    SET_VERIFICATION(state, verification) {
      state.verification = verification;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_ACTIVE_CHECK(state, checkId) {
      state.activeCheckId = checkId;
    },
    SET_VERIFIED_TAGS(state, tags) {
      state.verifiedTags = Array.isArray(tags) ? tags : [];
    },
    SET_TAGS_ERROR(state, message) {
      state.tagsError = message || null;
    },
    SET_LINKED_DOCUMENTS(state, documents) {
      state.linkedDocuments = Array.isArray(documents) ? documents : [];
    },
    SET_SELECTED_REFERENCE_DOCUMENT_ID(state, documentId) {
      state.selectedReferenceDocumentId = documentId || null;
    },
    SET_DOCUMENTS_LOADING(state, isLoading) {
      state.documentsLoading = !!isLoading;
    },
    SET_DOCUMENTS_ERROR(state, message) {
      state.documentsError = message || null;
    },
    SET_CONTEXT_REQUEST_ID(state, requestId) {
      state.contextRequestId = requestId || null;
    },

    /**
     * Store the resolved tag geometry.
     *
     * Frozen on the way in: the payload holds a live Forge model reference, and
     * Vue's observer would otherwise walk that entire object graph. `Object.freeze`
     * makes it non-extensible, which makes Vue skip it. The freeze is shallow, so
     * `bbox` stays a fully functional THREE.Box3. Reactivity comes from replacing
     * the whole object, never from mutating it in place.
     */
    SET_VERIFIED_GEOMETRY(state, geometry) {
      state.verifiedGeometry = geometry ? Object.freeze(geometry) : null;
    },

    /**
     * The resolved unit transform, or null while it is unknown.
     *
     * Plain assignment: the value arrives already frozen from
     * `buildUnitTransform`, and reactivity comes from replacing the whole
     * object. Null is a legitimate value, not a failure to commit — see the
     * state declaration.
     */
    SET_UNIT_TRANSFORM(state, transform) {
      state.unitTransform = transform || null;
    },

    /** Set the surveyor's explicit scan choice; null reverts to the auto pick. */
    SET_MANUAL_SCAN(state, scanName) {
      state.manualScanName = scanName || null;
    },
    SET_TAGS_LOADING(state, isLoading) {
      state.tagsLoading = !!isLoading;
    },
    SET_MODEL_READY(state, isReady) {
      state.modelReady = !!isReady;
    },
    SET_PREFLIGHT_APPLIED(state, isApplied) {
      state.preflightApplied = !!isApplied;
    },
    SET_CATALOGUE_FACILITY(state, facilityId) {
      state.catalogueFacilityId = facilityId || null;
    },
    SET_VIEWER_ERROR(state, message) {
      state.viewerError = message || null;
    },
    SET_ACTIVE_VIEW(state, viewId) {
      state.activeView = viewId || null;
    },
    /**
     * Reset the entire module state to initial values — including the captured
     * points and the label counter, so a fresh session restarts at P01. Called
     * from VerificationViewer.vue's beforeDestroy (route leave, the only call
     * site) so the next entry starts clean and no residue (points, tags,
     * active check, error) leaks across routes.
     */
    RESET(state) {
      Object.assign(state, INITIAL_STATE());
    },
  },
  actions: {
    // ---- Point model (AVA-475) ----

    /**
     * Make sure the facility's scan catalogue is in ROOT state before the
     * viewer pre-flight tries to choose a scan.
     *
     * WHY THIS EXISTS
     * `GET_SELECTED_SCAN` reads `rootState.pointClouds / projects / levels`,
     * but the only writers are `MainView.vue` and the root
     * `GET_FACILITY_TABS_DATA` action — neither of which runs on the
     * verification route (it declares its own `viewer-layout` and its own
     * `VerificationViewer`, so MainView never mounts). Measured live: 142
     * point clouds at the facility root, **0** inside `/verification/:taskId`.
     * `pickScanForTagPoint` then exits on its first guard, `GET_SELECTED_SCAN`
     * returns null, and the pre-flight silently falls into "model-only mode" —
     * no scan, no as-built data, nothing to verify against.
     *
     * The workspace previously only worked if the user happened to visit a
     * route that populated root state first. This makes it own its dependency.
     *
     * Deliberately NOT `GET_FACILITY_TABS_DATA`: that also pulls every tag,
     * annotation and scaffold and calls `syncTagsToViewer()` — far more work
     * and more side effects than choosing a scan needs.
     *
     * Each source is fetched only if absent, so entering from a route that
     * already loaded some of them costs nothing. Failures are logged and
     * swallowed per-source: a missing levels list must not stop the point
     * clouds from arriving.
     */
    async ENSURE_SCAN_CATALOGUE({ commit, state, rootState }, facilityId) {
      if (!facilityId || state.catalogueFacilityId === facilityId) return;
      // Claim before awaiting so two concurrent callers cannot double-fetch.
      commit('SET_CATALOGUE_FACILITY', facilityId);

      const jobs = [];

      if (!rootState.pointClouds?.length) {
        jobs.push(
          fetchAssetPointCloudModels(facilityId)
            .then((pointClouds) => commit('setPointClouds', pointClouds || [], { root: true }))
            .catch((err) => debugError('[VERIFICATION] point-cloud catalogue fetch failed', err)),
        );
      }

      if (!rootState.projects?.length) {
        jobs.push(
          projectsService
            .getByAsset(facilityId)
            .then((res) => {
              // Parse only — deliberately NOT calling setAllImagesVisibleInViewers,
              // which is a viewer-visibility side effect the verification
              // workspace must not trigger (it would light up 360 markers).
              commit('setProjects', ForgeProject.parseProjectsFromDB(res.data) || [], {
                root: true,
              });
            })
            .catch((err) => debugError('[VERIFICATION] projects fetch failed', err)),
        );
      }

      if (!rootState.levels?.length) {
        jobs.push(
          levelsService
            .fetchAssetLevelsParsed(facilityId)
            .then((levels) => commit('setLevels', levels || [], { root: true }))
            .catch((err) => debugError('[VERIFICATION] levels fetch failed', err)),
        );
      }

      await Promise.all(jobs);
      debugLog('[VERIFICATION] scan catalogue ready', {
        facilityId,
        pointClouds: rootState.pointClouds?.length ?? 0,
        projects: rootState.projects?.length ?? 0,
        levels: rootState.levels?.length ?? 0,
      });
    },

    /**
     * Resolve the verified tag's geometry against the loaded models and commit it.
     *
     * Must be an ACTION, not a getter. The inputs (`viewer`, its models, their
     * instance trees) are external Forge objects that Vue cannot observe, so a
     * getter computing this would cache its first answer permanently. Measured
     * live on a cold reload: the getter reported null while the identical
     * computation run by hand at that same instant returned a valid box.
     *
     * Idempotent and cheap to call repeatedly — the pre-flight retry loop drives
     * it, so it re-runs on every attempt until the geometry actually resolves.
     *
     * @returns {object | null} the resolved geometry, for callers that want it
     *   without a second store read.
     */
    RESOLVE_VERIFIED_GEOMETRY({ commit, getters, state, rootState }) {
      const resolved = resolveVerifiedBBox(rootState.viewer || null, getters.GET_VERIFIED_DBIDS);
      // Only commit on a real change — re-committing an equivalent result on
      // every poll would churn every downstream getter for no reason.
      const current = state.verifiedGeometry;
      const changed =
        !!resolved !== !!current ||
        (resolved &&
          current &&
          (resolved.dbIdKey !== current.dbIdKey ||
            resolved.modelId !== current.modelId ||
            resolved.fragCount !== current.fragCount));
      if (changed) commit('SET_VERIFIED_GEOMETRY', resolved);

      // The unit transform is built from the model this pass PROVED owns the
      // fragments — never from `rootState.viewer`, whose `.model` can be the
      // empty `{3D}` one on this route (utils/verifiedBBox.js:26-33). Gated
      // separately from the geometry: see `sameUnitTransform`.
      const nextTransform = resolved ? buildUnitTransform(resolved.model) : null;
      if (!sameUnitTransform(state.unitTransform, nextTransform)) {
        commit('SET_UNIT_TRANSFORM', nextTransform);
        debugLog('[VERIFICATION] unit transform resolved', {
          modelId: resolved?.modelId ?? null,
          mmPerViewerUnit: nextTransform?.mmPerViewerUnit ?? null,
          siteScale: nextTransform?.siteScale ?? null,
          siteOffset: nextTransform?.siteOffset ?? null,
        });
      }
      return resolved;
    },

    /**
     * Append an empty point and focus it. Internal ids remain monotonic while
     * display numbers fill the smallest available P-number slot.
     */
    addPoint({ commit, state }) {
      const pointNumber = nextAvailablePointNumber(state.points);
      commit('INCREMENT_POINT_COUNTER');
      const n = state.pointCounter;
      const point = {
        id: `pt-${n}`,
        pointNumber,
        label: formatDefaultPointName(pointNumber),
        comment: '',
        design: null,
        designSource: null,
        designOffset: null,
        asBuilt: null,
        asBuiltSource: null,
        asBuiltOffset: null,
        // As-built provenance, snapshotted at capture. A conformance report has
        // to be able to say WHICH cloud a reading came from and HOW COARSE the
        // view was when it was taken — the spike measured 145 mm of pick
        // scatter at building zoom against a ±3 mm tolerance.
        asBuiltScanName: null,
        asBuiltScanNames: [],
        asBuiltScanIsManual: false,
        asBuiltMmPerPx: null,
        asBuiltPlanes: null,
        // Viewer screenshots taken for this point. The HPC template carries a
        // system-populated Screenshot column, and the old check-centric
        // evidence slot was dropped when the model went point-centric — this
        // is that slot restored where it belongs, on the point.
        evidence: [],
        tolerance: { ...DEFAULT_TOLERANCE },
      };
      commit('ADD_POINT', point);
      commit('SET_ACTIVE_POINT', point.id);
      commit('SET_ACTIVE_STEP', 1);
      // A draft belongs to exactly one point. Cleared here in the ACTION rather
      // than inside SET_ACTIVE_POINT — that mutation owns the selection only.
      commit('CLEAR_AS_BUILT_DRAFT', point.id);
      commit('CLEAR_AXIS_LOCKS');
      commit('CLEAR_DESIGN_PICK_PREVIEW');
      return point;
    },

    renamePoint({ commit, state }, { id, label }) {
      const point = state.points.find((candidate) => candidate.id === id);
      if (!point) return;
      const fallback = formatDefaultPointName(
        point.pointNumber || nextAvailablePointNumber(state.points),
      );
      commit('PATCH_POINT', {
        id,
        patch: { label: normalizePointName(label, fallback) },
      });
    },

    setPointComment({ commit }, { id, comment }) {
      commit('PATCH_POINT', {
        id,
        patch: { comment: String(comment ?? '') },
      });
    },

    removePoint({ commit, state }, id) {
      commit('REMOVE_POINT', id);
      commit('CLEAR_MEASUREMENT_CROP_BOUNDS', id);
      if (state.designPickPreview?.pointId === id) commit('CLEAR_DESIGN_PICK_PREVIEW');
      // Only re-target the selection when the point we just removed was the
      // active one — removing a background point must not steal focus.
      if (state.activePointId === id) {
        commit('SET_ACTIVE_POINT', state.points[0]?.id ?? null);
        commit('CLEAR_AXIS_LOCKS');
      }
      // Drop a draft that belonged to the deleted point, whether or not it was
      // the active one.
      if (state.asBuiltDraft?.pointId === id) {
        commit('CLEAR_AS_BUILT_DRAFT', state.activePointId);
      }
    },

    selectPoint({ commit, state }, id) {
      const pointChanged = state.activePointId !== id;
      commit('SET_ACTIVE_POINT', id);
      if (pointChanged) commit('CLEAR_DESIGN_PICK_PREVIEW');
      // Half-finished picks belong to the point they were taken for. Reset in
      // the action so no mutation carries a hidden side effect.
      if (state.asBuiltDraft?.pointId !== id) {
        commit('CLEAR_AS_BUILT_DRAFT', id);
      }
      if (pointChanged) commit('CLEAR_AXIS_LOCKS');
    },

    setDesignPoint({ commit }, { id, point, source, offset = null }) {
      commit('CLEAR_MEASUREMENT_CROP_BOUNDS', id);
      commit('PATCH_POINT', {
        id,
        patch: { design: point, designSource: source, designOffset: offset },
      });
      // Locks are snapshots of design values. Editing the design coordinate
      // invalidates them, even if the same axis remains lockable.
      commit('CLEAR_AXIS_LOCKS');
      commit('CLEAR_DESIGN_PICK_PREVIEW');
    },

    /**
     * Record an as-built reading together with its provenance.
     *
     * FULL REPLACE, not a merge — exactly like `offset` already was. A second
     * call that omits `scanName` / `mmPerPx` NULLS the provenance rather than
     * keeping the old values, so the stored provenance can never describe a
     * different reading than the one beside it.
     *
     * @param {string} p.id            Point id ('pt-N').
     * @param {{x,y,z}} p.point        As-built position, VIEWER units.
     * @param {'cloudPick'|'manual'} p.source
     * @param {{x,y,z}|null} [p.offset]        VIEWER units — `adjustPoint` sums
     *                                         it straight into `point`, so it
     *                                         cannot be in any other space.
     * @param {string|null} [p.scanName]       Cloud the reading was taken against. A single
     *                                         LOOKUP-ABLE name (matches GET_SCAN_OPTIONS), or
     *                                         null when the reading spans more than one cloud.
     * @param {string[]|null} [p.scanNames]    Every cloud that contributed. Authoritative for
     *                                         the report; defaults to [scanName].
     * @param {boolean} [p.scanIsManual]       True when the surveyor chose that cloud by hand.
     * @param {number|null} [p.mmPerPx]        Worst-axis screen scale at capture, mm/px
     *                                         (a NUMBER — reduce {horiz,vert} before calling).
     * @param {string[]|null} [p.planes]       Slice planes that contributed ('XY','XZ','YZ').
     */
    setAsBuiltPoint(
      { commit },
      {
        id,
        point,
        source,
        offset = null,
        scanName = null,
        scanNames = null,
        scanIsManual = false,
        mmPerPx = null,
        planes = null,
        lockedAxes: locked = [],
        lockedValues = null,
      },
    ) {
      // Two fields, deliberately. `asBuiltScanName` stays a single name that can
      // be looked up in GET_SCAN_OPTIONS — it is null rather than a joined
      // string when several clouds contributed, because a fabricated
      // "A + B" identifier resolves to nothing and would read as a real scan.
      // `asBuiltScanNames` is the authoritative list; the report joins it for
      // display, which is presentation, not storage.
      const allNames = Array.isArray(scanNames)
        ? scanNames.filter(Boolean)
        : (scanName ? [scanName] : []);
      const singleName = scanName || (allNames.length === 1 ? allNames[0] : null);

      commit('PATCH_POINT', {
        id,
        patch: {
          asBuilt: point,
          asBuiltSource: source,
          asBuiltOffset: offset,
          asBuiltScanName: singleName,
          asBuiltScanNames: allNames,
          asBuiltScanIsManual: !!scanIsManual,
          asBuiltMmPerPx: typeof mmPerPx === 'number' && Number.isFinite(mmPerPx) ? mmPerPx : null,
          asBuiltPlanes: Array.isArray(planes) && planes.length ? [...planes] : null,
          // Axes asserted from design rather than measured. Always written —
          // an empty array is the honest record of "everything was measured",
          // and leaving the field undefined would make an old point and a
          // fully-measured new one indistinguishable.
          lockedAxes: Array.isArray(locked) ? [...locked] : [],
          lockedValues: lockedValues ? { ...lockedValues } : null,
        },
      });
      debugLog('[VERIFICATION/setAsBuiltPoint]', { id, source, scanName, mmPerPx, planes });
    },

    /**
     * Fold one slice view's pick into the as-built draft.
     *
     * The scan provenance defaults from the store AT PICK TIME so a caller
     * physically cannot forget it. Callers should still pass `mmPerPx`
     * (the screen scale reduced to its worst axis) — it is the only
     * evidence of how coarse the view was, and it cannot be recovered later.
     *
     * The AGREEMENT GATE compares two picks in millimetres, so the merge needs
     * the unit scale — and the mutation it commits to can never see it, because
     * a Vuex mutation receives only `(state, payload)`. So the scale is resolved
     * HERE, from `state.unitTransform`, and rides the payload. Resolving it
     * inside the mutation would mean reading the live viewer from a mutation,
     * which is the one thing this feature's unit contract forbids everywhere.
     *
     * @param {string} p.pointId
     * @param {'XY'|'XZ'|'YZ'} p.plane
     * @param {{x,y,z}} p.point   Picked point, VIEWER units (unconverted).
     * @param {number|null} [p.mmPerPx]
     * @param {string|null} [p.scanName]     Defaults to GET_SELECTED_SCAN?.name.
     * @param {boolean|null} [p.scanIsManual] Defaults to GET_SCAN_IS_MANUAL.
     * @returns {Object} The decorated draft status (see GET_AS_BUILT_DRAFT_STATUS).
     */
    mergeAsBuiltDraftAxes(
      { commit, getters, state },
      { pointId, plane, point, mmPerPx = null, scanName = null, scanIsManual = null },
    ) {
      commit('MERGE_AS_BUILT_DRAFT', {
        pointId,
        plane,
        point,
        mmPerPx,
        mmPerViewerUnit: state.unitTransform?.mmPerViewerUnit ?? null,
        scanName: scanName ?? getters.GET_SELECTED_SCAN?.name ?? null,
        scanIsManual: scanIsManual ?? getters.GET_SCAN_IS_MANUAL,
      });
      const status = getters.GET_AS_BUILT_DRAFT_STATUS;
      debugLog('[VERIFICATION/mergeAsBuiltDraftAxes]', {
        pointId,
        plane,
        filled: status.filledAxes,
        missing: status.missingAxes,
        isComplete: status.isComplete,
      });
      return status;
    },

    clearAsBuiltDraft({ commit }, pointId = null) {
      commit('CLEAR_AS_BUILT_DRAFT', pointId);
    },

    /**
     * Return a completed point to the as-built stage without touching its
     * design coordinate or tolerance. This powers the wizard's explicit
     * "Remeasure" action and clears every field that describes the superseded
     * reading so old provenance can never survive beside the new value.
     */
    resetAsBuiltPoint({ commit, state }, id) {
      if (!state.points.some((point) => point.id === id)) return false;

      commit('PATCH_POINT', {
        id,
        patch: {
          asBuilt: null,
          asBuiltSource: null,
          asBuiltOffset: null,
          asBuiltScanName: null,
          asBuiltScanNames: [],
          asBuiltScanIsManual: false,
          asBuiltMmPerPx: null,
          asBuiltPlanes: null,
          lockedAxes: [],
          lockedValues: null,
        },
      });
      commit('CLEAR_AS_BUILT_DRAFT', id);
      commit('CLEAR_AXIS_LOCKS');
      commit('SET_ACTIVE_STEP', 2);
      return true;
    },

    /**
     * Commit a COMPLETE draft as the point's as-built reading.
     *
     * Refuses (and keeps the draft) when an axis is still missing or when the
     * target point no longer exists — PATCH_POINT no-ops silently on an unknown
     * id, and throwing three hard-won picks away without a word would be worse
     * than the stale id itself. The draft is cleared only after the patch lands.
     *
     * @param {string} p.id                Point id; defaults to the draft's own.
     * @param {{x,y,z}|null} [p.offset]
     * @returns {boolean} True when the as-built was written.
     */
    commitAsBuiltDraft({ commit, state, getters, dispatch }, { id = null, offset = null } = {}) {
      const draft = state.asBuiltDraft;
      const status = getters.GET_AS_BUILT_DRAFT_STATUS;
      const targetId = id || draft?.pointId || null;

      // THE ONLY PLACE A LOCKED VALUE BECOMES PART OF A POINT (RULE 2).
      // It happens here, at commit, AFTER the agreement gate has run on the
      // measured axes alone. `draftToPoint` deliberately still refuses an
      // under-filled draft — with locks the free axes must all be picked, so
      // the union is what completes it, never what excuses it.
      const measured = draftToPoint(draft);
      const point = status.lockedAxes?.length
        ? (status.isCommittable ? applyLocks(draft.axes, state.asBuiltLocks) : null)
        : measured;

      if (!targetId || !point) {
        debugWarn('[VERIFICATION/commitAsBuiltDraft] draft incomplete', {
          targetId,
          missing: status.missingAxes,
        });
        return false;
      }
      if (!state.points.some((p) => p.id === targetId)) {
        debugWarn('[VERIFICATION/commitAsBuiltDraft] no such point — draft kept', targetId);
        return false;
      }

      // A draft assembled across two different clouds is legitimate but must be
      // reported as such — silently keeping only the first scan name would put
      // a wrong single provenance on a conformance document.
      if (status.scanNames.length > 1) {
        debugWarn('[VERIFICATION/commitAsBuiltDraft] draft spans multiple scans', status.scanNames);
      }

      dispatch('setAsBuiltPoint', {
        id: targetId,
        point,
        source: AS_BUILT_SOURCE.CLOUD_PICK,
        offset,
        scanName: status.scanNames.length === 1 ? status.scanNames[0] : null,
        scanNames: status.scanNames,
        scanIsManual: status.scanIsManual,
        mmPerPx: status.worstMmPerPx,
        planes: status.planes,
        // RULE 1's paper trail. Which axes were asserted rather than measured
        // travels WITH the point, so the result card and the report can refuse
        // to call them PASS. Derived state cannot carry this — by the time the
        // report renders, the capture's lock state is long gone.
        lockedAxes: status.lockedAxes || [],
        lockedValues: status.lockedAxes?.length ? { ...state.asBuiltLocks } : null,
      });
      commit('CLEAR_AS_BUILT_DRAFT', targetId);
      return true;
    },

    setOffset({ commit }, { id, which, offset }) {
      const key = which === 'design' ? 'designOffset' : 'asBuiltOffset';
      commit('PATCH_POINT', { id, patch: { [key]: offset } });
    },

    setTolerance({ commit }, { id, tolerance }) {
      commit('PATCH_POINT', { id, patch: { tolerance: { ...tolerance } } });
    },

    setNotes({ commit }, notes) {
      commit('SET_NOTES', notes);
    },

    setReportOverride({ commit }, { key, value }) {
      commit('SET_REPORT_OVERRIDE', { key, value });
    },

    /**
     * Resolve the selected reference drawing into a printable report page.
     *
     * Dispatched when the report preview opens rather than on selection: it
     * fetches and decodes a file, and most sessions never open the report at
     * all. Re-resolving on every open also means a re-selected drawing is
     * always the one that prints.
     *
     * Resolving to null is NORMAL — a PDF or Office reference cannot be
     * rendered to a single page from here (see utils/referenceDrawing.js), so
     * the report simply carries no drawing page. Never an error to the user.
     */
    async RESOLVE_REPORT_DRAWING({ commit, getters }) {
      const display = getters.GET_DRAWING_DISPLAY;
      const drawing = await resolveReferenceDrawing(display?.document || null, display?.name || '');
      commit('SET_REPORT_DRAWING', drawing);
      return drawing;
    },

    /**
     * Photograph the viewer as it stands and file the image under the active
     * point.
     *
     * The viewer comes from `rootState`, never from the payload — every other
     * action in this module reads it that way, and a component passing its own
     * reference is how two viewers end up disagreeing about which one is live.
     *
     * GATED ON 'ready', NOT on a looser readiness flag. `GET_VIEWER_STATUS`
     * reports 'isolating' while the model is up but the crop / scan pre-flight
     * has not landed: a shot taken then shows the whole building instead of the
     * isolated support, which is a plausible-looking and completely useless
     * piece of evidence.
     *
     * A failure records NOTHING and says so. Half-captured evidence on a
     * conformance record is worse than absent evidence.
     *
     * NOTHING IS HIDDEN BEFORE THE SHOT. The ratified model is literal
     * WYSIWYG — axes, labels, markers and the crop box if the surveyor has it
     * showing all belong in the image, because that is what they were looking
     * at when they judged the reading.
     *
     * @returns {Promise<boolean>} true when an image was stored.
     */
    async CAPTURE_POINT_EVIDENCE({ commit, getters, rootState }) {
      const point = getters.GET_ACTIVE_POINT;
      if (!point) {
        debugWarn('[VERIFICATION/CAPTURE_POINT_EVIDENCE] no active point — nothing to attach to');
        return false;
      }
      const status = getters.GET_VIEWER_STATUS;
      if (status !== 'ready') {
        debugWarn('[VERIFICATION/CAPTURE_POINT_EVIDENCE] viewer not ready', { status });
        return false;
      }

      try {
        const shot = await captureViewerEvidence(rootState.viewer || null);
        commit('ADD_POINT_EVIDENCE', {
          pointId: point.id,
          evidence: {
            ...shot,
            // The plane vocabulary the slice tabs and the view combo already
            // share ('XY' | 'XZ' | 'YZ' | '3D'), so a caption never invents a
            // fourth name for a view the surveyor knows by one of these.
            viewLabel: getters.GET_ACTIVE_CAPTURE_PLANE,
          },
        });
        debugLog('[VERIFICATION/CAPTURE_POINT_EVIDENCE] stored', {
          point: point.label,
          view: getters.GET_ACTIVE_CAPTURE_PLANE,
          // Says whether the image came from the live frame (overlays included)
          // or the offscreen fallback (overlays missing) — the one thing you
          // need when an evidence image looks wrong.
          capturePath: shot.capturePath,
        });
        return true;
      } catch (err) {
        debugError('[VERIFICATION/CAPTURE_POINT_EVIDENCE] capture failed', err);
        return false;
      }
    },

    removePointEvidence({ commit }, { pointId, evidenceId }) {
      commit('REMOVE_POINT_EVIDENCE', { pointId, evidenceId });
    },

    // ---- Verification VM + viewer pre-flight (AVA-337) ----

    /**
     * Load the verification workspace view-model for a given task.
     * Sets loading → resolves via service → commits result.
     * On error, commits error string and clears verification.
     */
    async LOAD_VERIFICATION({
      commit, dispatch, rootState, state,
    }, taskId) {
      if (state.verification?.taskId && state.verification.taskId !== taskId) {
        commit('RESET');
      }
      const requestId = `${taskId}:${++contextRequestSequence}`;
      commit('SET_CONTEXT_REQUEST_ID', requestId);
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      commit('SET_TAGS_ERROR', null);
      commit('SET_DOCUMENTS_ERROR', null);
      commit('SET_LINKED_DOCUMENTS', []);
      commit('SET_SELECTED_REFERENCE_DOCUMENT_ID', null);
      commit('SET_VERIFIED_TAGS', []);
      commit('SET_VERIFIED_GEOMETRY', null);
      // Cleared WITH the geometry, always: the next facility may be authored in
      // different units, and briefly serving the previous transform would scale
      // the new support's first readings by the old model's answer.
      commit('SET_UNIT_TRANSFORM', null);
      commit('SET_SUPPORT', null);
      commit('SET_ACTIVE_CHECK', null);
      commit('SET_MANUAL_SCAN', null);
      commit('CLEAR_AS_BUILT_DRAFT', null);
      try {
        debugLog('[VERIFICATION/LOAD_VERIFICATION] dispatching for task', taskId);
        const taskResponse = await verificationService.getByTaskId(taskId);
        if (state.contextRequestId !== requestId) return;
        const task = taskResponse?.data ?? taskResponse;
        const verification = taskToVerificationContext(task);
        if (!verification) throw new Error('Verification task was not found');

        commit('SET_VERIFICATION', verification);
        const tagIds = verification.linkedTagIds || [];
        debugLog('[VERIFICATION/LOAD_VERIFICATION] task context loaded', {
          taskId,
          tagCount: tagIds.length,
        });

        const tags = tagIds.length
          ? await dispatch('LOAD_VERIFIED_TAGS', { tagIds, requestId })
          : [];
        if (state.contextRequestId !== requestId) return;
        const taskTags = verification.linkedTags || [];
        const primaryTag = tags?.find((tag) => tag?.id === tagIds[0])
          || tags?.[0]
          || taskTags.find((tag) => tag?.id === tagIds[0])
          || taskTags[0]
          || null;

        if (primaryTag) {
          commit('SET_SUPPORT', {
            supportNo: primaryTag.name || verification.taskFriendlyId || '',
            discipline: primaryTag.discipline || verification.taskType?.name || '',
            building: primaryTag.plant || primaryTag.unit || '',
            room: tagLocationLabel(primaryTag),
            handbookNo: '',
            modelVersion: '',
            toleranceSpec: '',
            reportId: verification.taskFriendlyId || verification.taskId,
            revision: '',
          });
        } else {
          commit('SET_VERIFIED_TAGS', []);
        }
        await dispatch('LOAD_LINKED_DOCUMENTS', {
          tagId: primaryTag?.id || null,
          taskId: verification.taskId,
          assetId: rootState.assetId || primaryTag?.forgeAssetId || null,
          requestId,
        });
      } catch (err) {
        if (state.contextRequestId !== requestId) return;
        debugError('[VERIFICATION/LOAD_VERIFICATION] failed', err);
        commit('SET_ERROR', err?.message || 'Failed to load verification workspace');
        commit('SET_VERIFICATION', null);
      } finally {
        if (state.contextRequestId === requestId) commit('SET_LOADING', false);
      }
    },
    /**
     * Fetch real ForgeBaseTag instances (with `.children[].dbId`) for the
     * given tag ids via the root-level GET_BASE_TAGS_BY_IDS action. Populates
     * `state.verifiedTags`, which downstream getters use to derive dbIds,
     * bbox, crop box, and selected scan.
     *
     * Graceful failure: if the backend returns empty or errors, the task's
     * embedded tag still drives the Context panel while downstream geometry
     * getters return null and the viewer skips isolation.
     */
    async LOAD_VERIFIED_TAGS({
      commit, dispatch, state,
    }, payload) {
      const tagIds = Array.isArray(payload) ? payload : payload?.tagIds;
      const requestId = Array.isArray(payload) ? null : payload?.requestId;
      const isCurrentRequest = () => !requestId || state.contextRequestId === requestId;
      if (!Array.isArray(tagIds) || tagIds.length === 0) {
        if (isCurrentRequest()) commit('SET_VERIFIED_TAGS', []);
        return [];
      }
      if (!isCurrentRequest()) return [];
      commit('SET_TAGS_LOADING', true);
      commit('SET_TAGS_ERROR', null);
      try {
        debugLog('[VERIFICATION/LOAD_VERIFIED_TAGS] fetching tags', tagIds);
        const tags = await dispatch('GET_BASE_TAGS_BY_IDS', tagIds, { root: true });
        if (!isCurrentRequest()) return [];
        commit('SET_VERIFIED_TAGS', tags || []);
        debugLog('[VERIFICATION/LOAD_VERIFIED_TAGS] loaded', {
          count: (tags || []).length,
          totalChildren: (tags || []).reduce((sum, t) => sum + (t.children?.length || 0), 0),
        });
        return tags || [];
      } catch (err) {
        if (!isCurrentRequest()) return [];
        debugError('[VERIFICATION/LOAD_VERIFIED_TAGS] failed', err);
        commit('SET_VERIFIED_TAGS', []);
        commit('SET_TAGS_ERROR', err?.message || 'Linked tag details could not be loaded.');
        return [];
      } finally {
        if (isCurrentRequest()) commit('SET_TAGS_LOADING', false);
      }
    },
    async LOAD_LINKED_DOCUMENTS({
      commit, state,
    }, {
      tagId, taskId, assetId, requestId = null,
    }) {
      const isCurrentRequest = () => !requestId || state.contextRequestId === requestId;
      if (!tagId && !taskId) {
        if (isCurrentRequest()) commit('SET_LINKED_DOCUMENTS', []);
        if (isCurrentRequest()) commit('SET_SELECTED_REFERENCE_DOCUMENT_ID', null);
        return [];
      }
      if (!isCurrentRequest()) return [];
      commit('SET_DOCUMENTS_LOADING', true);
      commit('SET_DOCUMENTS_ERROR', null);
      try {
        const requests = [
          tagId
            ? verificationService.getDocumentsForTag({ tagId, assetId })
            : Promise.resolve({ items: [], total: 0 }),
          taskId
            ? verificationService.getDocumentsForTask({ taskId, assetId })
            : Promise.resolve({ items: [], total: 0 }),
        ];
        const [tagResult, taskResult] = await Promise.allSettled(requests);
        if (!isCurrentRequest()) return [];
        const tagDocuments = tagResult.status === 'fulfilled'
          ? tagResult.value?.items || []
          : [];
        const taskDocuments = taskResult.status === 'fulfilled'
          ? taskResult.value?.items || []
          : [];
        const documents = mergeReferenceDocuments({
          tagDocuments,
          taskDocuments,
          tagId,
        });
        commit('SET_LINKED_DOCUMENTS', documents);
        const previousSelection = documents.find(
          (document) => documentIdentity(document) === state.selectedReferenceDocumentId,
        );
        const automaticSelection = previousSelection
          || findLinkedIsoDocument(documents, getPrimaryVerificationTag(state));
        commit(
          'SET_SELECTED_REFERENCE_DOCUMENT_ID',
          documentIdentity(automaticSelection),
        );
        if (tagResult.status === 'rejected' || taskResult.status === 'rejected') {
          commit(
            'SET_DOCUMENTS_ERROR',
            documents.length
              ? 'Some related documents could not be loaded.'
              : 'Related documents could not be loaded.',
          );
        }
        return documents;
      } catch (err) {
        if (!isCurrentRequest()) return [];
        debugError('[VERIFICATION/LOAD_LINKED_DOCUMENTS] failed', err);
        commit('SET_LINKED_DOCUMENTS', []);
        commit('SET_SELECTED_REFERENCE_DOCUMENT_ID', null);
        commit('SET_DOCUMENTS_ERROR', err?.message || 'Related documents could not be loaded.');
        return [];
      } finally {
        if (isCurrentRequest()) commit('SET_DOCUMENTS_LOADING', false);
      }
    },
    selectReferenceDocument({ commit, state }, documentId) {
      const exists = state.linkedDocuments.some(
        (document) => documentIdentity(document) === documentId,
      );
      commit('SET_SELECTED_REFERENCE_DOCUMENT_ID', exists ? documentId : null);
    },
    SELECT_CHECK({ commit }, checkId) {
      debugLog('[VERIFICATION/SELECT_CHECK]', checkId);
      commit('SET_ACTIVE_CHECK', checkId);
    },
  },
};

/**
 * True when an offset vector exists AND actually shifts the point. A zero
 * offset is treated as "no offset" so the Step 4 offset-check view only opens
 * when both sides were genuinely measured from a reference face.
 */
/**
 * Delegates to the shared threshold rather than testing `!== 0`. A 0.0004 m
 * offset is non-zero but renders as `+0.000` everywhere it is displayed, so
 * treating it as an offset lights up the offset-check view for an adjustment
 * the surveyor cannot see. See OFFSET_EPSILON_M in pointMath.js.
 */
function hasOffset(o) {
  return hasSignificantOffset(o);
}
